import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Box,
  Chip,
  Autocomplete,
  TextField,
} from "@mui/material";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { exportExcelFile } from "utils/exportExcelFile";
import { useAuth } from "hooks";
import moment from "moment";
import { useLazyListShipmentStaleQuery } from "store/services/shipmentApi";
import { useSearchParams } from "react-router-dom";

const EXCEL_DATA_INITIAL_STATE = {
  sourceBranchId: null,
  destinationBranchId: null,
  statuses: [],
};

// Exclude "Delivered" status as per requirement
const SHIPMENT_STATUSES = [
  "Pending",
  "Collected",
  "Consolidation",
  "Loaded",
  "Unloaded",
  "Deconsolidation",
  "CrossDocked",
  "SendOut",
  "Warehousing",
  "DeliveryFailed",
  "Return",
  "Cancelled",
];

const ExportStaleShipmentExcel = ({ isBusy = false }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [showSourceBranchForm, setShowSourceBranchForm] = useState(false);
  const [showDestinationBranchForm, setShowDestinationBranchForm] =
    useState(false);
  const [searchParams] = useSearchParams();
  const { viewEntity, isVendor, isCustomer } = useAuth();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: EXCEL_DATA_INITIAL_STATE,
  });

  const [
    fetchStaleShipments,
    { isLoading: loadingShipments, isFetching: fetchingShipments },
  ] = useLazyListShipmentStaleQuery();
  const [fetchBranches, { data: branchesData, isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();

  useEffect(() => {
    if (!showDialog) return;

    const currentStatus = searchParams.get("shipmentStatus");
    reset({
      ...EXCEL_DATA_INITIAL_STATE,
      ...(currentStatus && { statuses: [currentStatus] }),
    });

    if (viewEntity?.entity?._id && viewEntity?.entity?.account?._id) {
      fetchBranches({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
    }
  }, [
    fetchBranches,
    viewEntity.entity._id,
    viewEntity.entity.account._id,
    showDialog,
    searchParams,
    reset,
  ]);

  const handleDownload = async (data) => {
    const { sourceBranchId, destinationBranchId, statuses } = data;
    const keyword = searchParams.get("search");
    const urlSourceBranchId = searchParams.get("sourceBranch");
    const urlDestinationBranchId = searchParams.get("destinationBranch");
    const urlCurrentBranchId = searchParams.get("currentBranch");
    const urlCurrentRegionId = searchParams.get("currentRegion");
    const [sortedBy, sortDirection] = (
      searchParams.get("sortOrder") || "createdAt:-1"
    ).split(":");

    const payload = {
      ...(keyword && { keyword }),
      sortedBy,
      sortDirection: parseInt(sortDirection),
      ...((sourceBranchId?._id || urlSourceBranchId) && {
        sourceBranchId: sourceBranchId?._id || urlSourceBranchId,
      }),
      ...((destinationBranchId?._id || urlDestinationBranchId) && {
        destinationBranchId: destinationBranchId?._id || urlDestinationBranchId,
      }),
      ...(urlCurrentBranchId && { currentBranchId: urlCurrentBranchId }),
      ...(urlCurrentRegionId && { currentRegionId: urlCurrentRegionId }),
      ...(statuses.length > 0 && { status: statuses.join(",") }),
      limit: 10000, // Set high limit to get all records
      page: 1,
    };

    try {
      const responseData = await fetchStaleShipments(payload).unwrap();

      if (!responseData?.data || responseData.data.length === 0) {
        toast.info(
          "No unattended shipment records found for the selected filters."
        );
        return;
      }

      const formattedData = responseData.data.map((row, index) => {
        const hours = Math.floor(row.hoursSinceLastProcess || 0);
        const days = Math.floor(hours / 24);
        const unattendedDuration =
          days > 0 ? `${days}d ${hours % 24}h` : `${hours}h`;

        return {
          SN: index + 1,
          CreatedAt: row.createdAt
            ? moment(row.createdAt).format("YYYY-MM-DD HH:mm:ss")
            : "N/A",
          "Shipment ID": row.shipmentId || "N/A",
          "Source Branch": row.sourceBranch?.name || "N/A",
          "Destination Branch": row.destinationBranch?.name || "N/A",
          "Current Branch": row.currentBranch?.name || "N/A",
          "Branch Basis": row.currentBranchType || "N/A",
          Receiver:
            row.receiver?.customerUser?.name || row.receiver?.name || "N/A",
          Sender:
            !isVendor && !isCustomer
              ? row.customerId?.customerUser?.name
                ? `CUS: ${row.customerId.customerUser.name}`
                : row.vendor?.shopName
                  ? `VEN: ${row.vendor.shopName}`
                  : "N/A"
              : "N/A",
          "Shipment Status": row.status || "N/A",
          "Unattended Duration": unattendedDuration,
          "Last Process Date": row.lastProcessDate
            ? moment(row.lastProcessDate).format("YYYY-MM-DD HH:mm:ss")
            : "N/A",
          "Created By": row.createdBy?.name || "N/A",
        };
      });

      exportExcelFile({
        sheetData: formattedData,
        fileName: `Unattended_Shipments_${moment().format("YYYYMMDD")}`,
      });

      toast.success(
        `Successfully downloaded ${formattedData.length} unattended shipment records`
      );
    } catch (error) {
      console.error("Error fetching unattended shipment data:", error);
      toast.error("Error fetching unattended shipment data: " + error.message);
    }
    handleReset();
  };

  const handleReset = () => {
    setShowDialog(false);
    setShowSourceBranchForm(false);
    setShowDestinationBranchForm(false);
    reset(EXCEL_DATA_INITIAL_STATE);
  };

  return (
    <>
      <Tooltip title="Export Excel" arrow>
        <IconButton color="success" onClick={() => setShowDialog(true)}>
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>

      <AppConfirmDialog
        open={showDialog}
        onClose={handleReset}
        onConfirm={handleSubmit(handleDownload)}
        title="Download Unattended Shipment Excel"
        loading={loadingShipments || fetchingShipments}
        dialogIcon={() => <FileDownloadOutlined color="success" />}
        sx={{ "& .MuiDialog-paper": { maxWidth: "600px", width: "100%" } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 1 }}>
          {/* Branch Filter Checkboxes */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showSourceBranchForm}
                  onChange={(e) => setShowSourceBranchForm(e.target.checked)}
                  icon={<CheckBoxOutlineBlankOutlined />}
                  checkedIcon={<CheckBoxOutlined />}
                  size="small"
                />
              }
              label="Source Branch"
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showDestinationBranchForm}
                  onChange={(e) =>
                    setShowDestinationBranchForm(e.target.checked)
                  }
                  icon={<CheckBoxOutlineBlankOutlined />}
                  checkedIcon={<CheckBoxOutlined />}
                  size="small"
                />
              }
              label="Dest. Branch"
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
              }}
            />
          </Box>

          {/* Branch Selection Forms */}
          {(showSourceBranchForm || showDestinationBranchForm) && (
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}
            >
              {showSourceBranchForm && (
                <Controller
                  name="sourceBranchId"
                  control={control}
                  rules={{
                    required: showSourceBranchForm
                      ? "Please select Source Branch."
                      : false,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      label="Source Branch"
                      required={showSourceBranchForm}
                      value={value || null}
                      onChange={(_, data) => onChange(data)}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={branchesData || []}
                      loading={loadingBranches}
                      loadingText="Loading Branches..."
                      helperText={errors?.sourceBranchId?.message}
                      error={!!errors?.sourceBranchId}
                      size="small"
                    />
                  )}
                />
              )}
              {!showSourceBranchForm && showDestinationBranchForm && <Box />}
              {showDestinationBranchForm && (
                <Controller
                  name="destinationBranchId"
                  control={control}
                  rules={{
                    required: showDestinationBranchForm
                      ? "Please select Destination Branch."
                      : false,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      label="Destination Branch"
                      required={showDestinationBranchForm}
                      value={value || null}
                      onChange={(_, data) => onChange(data)}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={branchesData || []}
                      loading={loadingBranches}
                      loadingText="Loading Branches..."
                      helperText={errors?.destinationBranchId?.message}
                      error={!!errors?.destinationBranchId}
                      size="small"
                    />
                  )}
                />
              )}
            </Box>
          )}

          {/* Status Filter */}
          <Controller
            name="statuses"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={SHIPMENT_STATUSES}
                value={field.value}
                onChange={(_, value) => field.onChange(value)}
                disableCloseOnSelect
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Status"
                    size="small"
                    sx={{ "& .MuiInputBase-root": { fontSize: "0.875rem" } }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                      sx={{
                        m: 0.5,
                        height: 24,
                        "& .MuiChip-label": { fontSize: "0.75rem" },
                      }}
                    />
                  ))
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      icon={<CheckBoxOutlineBlankOutlined fontSize="small" />}
                      checkedIcon={<CheckBoxOutlined fontSize="small" />}
                      sx={{ p: 0.5 }}
                    />
                    <span style={{ fontSize: "0.875rem" }}>{option}</span>
                  </li>
                )}
                sx={{ width: "100%" }}
                ListboxProps={{ style: { maxHeight: 200 } }}
              />
            )}
          />
        </Box>
      </AppConfirmDialog>
    </>
  );
};

export default ExportStaleShipmentExcel;
